import { PALETTE, sline, scircle, sellipse, fillPoly, shadow, stext } from './sketch.js';

/**
 * 简笔画玩家：四向行走 / 盘坐 / 躺卧，程序化骨骼动画（无贴图）。
 * 锚点 (x, y) = 脚底中心（坐下时是臀部着地点，躺下时是身体接床的那条线）。
 * o.pose: 'stand' | 'sit' | 'lie'
 * o.z   : 离地高度（起跳），影子留在地面并随之缩小
 */
const H = 32;      // 身高
const HEAD_R = 5.6;

export function drawPlayer(ctx, x, y, o = {}) {
  const pose = o.pose || 'stand';
  const z = o.z || 0;
  if (pose === 'lie') return drawLie(ctx, x, y, o, z);
  if (pose === 'sit') return drawSit(ctx, x, y, o, z);
  return drawStand(ctx, x, y, o, z);
}

function drawStand(ctx, x, y0, o, z) {
  const facing = o.facing || 'down';
  const moving = !!o.moving;
  const phase = o.phase || 0;
  const t = o.time || 0;

  const swing = moving ? Math.sin(phase) : 0;
  const bob = moving ? Math.abs(Math.sin(phase)) * 1.4 : Math.sin(t * 2) * 0.5;

  const k = Math.max(0.5, 1 - z / 46);        // 跳得越高影子越小
  shadow(ctx, x, y0 + 1, 8 * k, 3 * k);

  const y = y0 - z;                            // 人离地，影子不动
  const footY = y;
  const hipY = y - 12 + bob;
  const shY = y - 21 + bob;
  const headY = y - 27 + bob;

  const side = facing === 'left' || facing === 'right';
  const faceLeft = facing === 'left';
  const dir = faceLeft ? -1 : 1;

  // 腿
  if (side) {
    const s = swing * 4;
    sline(ctx, x - 1, hipY, x - 1 + s * 0.6, footY, { color: PALETTE.ink, width: 2.2, seed: 1 });
    sline(ctx, x + 1, hipY, x + 1 - s * 0.6, footY, { color: PALETTE.ink, width: 2.2, seed: 2 });
  } else {
    const s = swing * 5;
    sline(ctx, x - 3, hipY, x - 3 + s, footY, { color: PALETTE.ink, width: 2.2, seed: 3 });
    sline(ctx, x + 3, hipY, x + 3 - s, footY, { color: PALETTE.ink, width: 2.2, seed: 4 });
  }

  drawRobe(ctx, x, shY, hipY, side);

  // 手臂（与腿反相）
  const armS = -swing * 4;
  if (side) {
    sline(ctx, x - 2, shY + 1, x - 2 + armS * 0.5, shY + 9, { color: PALETTE.ink, width: 1.8, seed: 8 });
    sline(ctx, x + 2, shY + 1, x + 2 - armS * 0.5, shY + 9, { color: PALETTE.ink, width: 1.8, seed: 9 });
  } else {
    const rt = side ? 3.2 : 4;
    sline(ctx, x - rt, shY + 1, x - rt - 1.5 + armS, shY + 10, { color: PALETTE.ink, width: 1.8, seed: 10 });
    sline(ctx, x + rt, shY + 1, x + rt + 1.5 - armS, shY + 10, { color: PALETTE.ink, width: 1.8, seed: 11 });
  }

  const headX = x + (side ? dir * 0.8 : 0);
  drawHead(ctx, headX, headY, facing, dir, side, 12);
}

/** 盘坐：腿交叉在身前，手结印，身子矮一截 */
function drawSit(ctx, x, y0, o, z) {
  const facing = o.facing || 'down';
  const t = o.time || 0;
  const bob = Math.sin(t * 1.4) * 0.6;         // 呼吸起伏
  const y = y0 - z;

  shadow(ctx, x, y0 + 1, 9.5, 3.2);

  const hipY = y - 8 + bob;
  const shY = y - 19 + bob;
  const headY = y - 25 + bob;
  const side = facing === 'left' || facing === 'right';
  const dir = facing === 'left' ? -1 : 1;

  // 盘着的腿：一个扁椭圆 + 两只交叠的脚
  sellipse(ctx, x, hipY + 2, 8.5, 3.2, { color: PALETTE.ink, width: 1.7, seed: 21, fill: '#e5dcc8', seg: 13, amp: 0.4 });
  sline(ctx, x - 7, hipY + 1.4, x + 7, hipY + 2.6, { color: PALETTE.inkSoft, width: 1, alpha: 0.45, seed: 22 });
  scircle(ctx, x - 4.2, hipY + 3.6, 1.9, { color: PALETTE.ink, width: 1.1, seed: 23, fill: '#f7e9d2', seg: 7 });
  scircle(ctx, x + 4.2, hipY + 3.6, 1.9, { color: PALETTE.ink, width: 1.1, seed: 24, fill: '#f7e9d2', seg: 7 });

  drawRobe(ctx, x, shY, hipY + 1, side);

  // 手：垂到膝上，身前结印
  const rt = side ? 3.2 : 4;
  sline(ctx, x - rt, shY + 1, x - 3, shY + 8, { color: PALETTE.ink, width: 1.8, seed: 25 });
  sline(ctx, x + rt, shY + 1, x + 3, shY + 8, { color: PALETTE.ink, width: 1.8, seed: 26 });
  scircle(ctx, x, shY + 9.6, 2.5, { color: PALETTE.inkSoft, width: 1, alpha: 0.75, seed: 27, seg: 9 });

  const headX = x + (side ? dir * 0.8 : 0);
  drawHead(ctx, headX, headY, facing, dir, side, 28);
}

/** 躺卧：横躺着，被子盖到胸口 */
function drawLie(ctx, x, y0, o, z) {
  const facing = o.facing || 'up';
  const t = o.time || 0;
  const dir = facing === 'left' ? -1 : 1;      // 头朝哪一侧
  const breath = Math.sin(t * 1.1) * 0.7;
  const y = y0 - z;

  shadow(ctx, x, y0 + 1, 15, 3.2);

  const bodyY = y - 5 + breath;
  const headX = x - dir * 14;
  const footX = x + dir * 11;

  // 身体（道袍，横躺）
  fillPoly(ctx, [
    [x - dir * 9, bodyY - 6], [footX, bodyY - 4],
    [footX, bodyY + 3], [x - dir * 9, bodyY + 4],
  ], '#eae3d2', 0.9);
  sline(ctx, x - dir * 9, bodyY - 6, footX, bodyY - 4, { color: PALETTE.ink, width: 1.5, seed: 41 });
  sline(ctx, x - dir * 9, bodyY + 4, footX, bodyY + 3, { color: PALETTE.ink, width: 1.5, seed: 42 });

  // 被子：盖住下半身，随呼吸起伏
  const qx0 = x - dir * 2;
  fillPoly(ctx, [
    [qx0, bodyY - 6.5], [footX + dir * 2, bodyY - 5],
    [footX + dir * 2, bodyY + 4], [qx0, bodyY + 4.5],
  ], PALETTE.cloth, 0.5);
  sline(ctx, qx0, bodyY - 6.5, footX + dir * 2, bodyY - 5, { color: PALETTE.cloth, width: 1.4, seed: 43, amp: 1.2 });
  sline(ctx, qx0, bodyY + 4.5, footX + dir * 2, bodyY + 4, { color: PALETTE.inkSoft, width: 0.9, alpha: 0.4, seed: 44 });

  // 露在外面的手（搭在被子边）
  sline(ctx, x - dir * 1, bodyY - 4, x + dir * 4, bodyY - 1, { color: PALETTE.ink, width: 1.7, seed: 45 });

  // 头（脸朝天，按正面画）
  drawHead(ctx, headX, bodyY - 1, 'down', dir, false, 46);
}

/** 道袍：上身梯形 + 腰带 */
function drawRobe(ctx, x, shY, hipY, side) {
  const robeTop = side ? 3.2 : 4;
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = '#eae3d2';
  ctx.beginPath();
  ctx.moveTo(x - robeTop, shY);
  ctx.lineTo(x + robeTop, shY);
  ctx.lineTo(x + 5.5, hipY + 2);
  ctx.lineTo(x - 5.5, hipY + 2);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  sline(ctx, x - robeTop, shY, x - 5.5, hipY + 2, { color: PALETTE.ink, width: 1.6, seed: 5 });
  sline(ctx, x + robeTop, shY, x + 5.5, hipY + 2, { color: PALETTE.ink, width: 1.6, seed: 6 });
  sline(ctx, x - (robeTop + 1.4), shY + 6, x + (robeTop + 1.4), shY + 6, { color: PALETTE.cloth, width: 1.8, seed: 7 });
}

/** 头 + 道髻 + 五官 */
function drawHead(ctx, headX, headY, facing, dir, side, seed0) {
  scircle(ctx, headX, headY, HEAD_R, { color: PALETTE.ink, width: 1.8, seed: seed0, fill: '#f7e9d2', seg: 14, amp: 0.5 });
  scircle(ctx, headX, headY - HEAD_R - 1.6, 2.2, { color: PALETTE.ink, width: 1.4, seed: seed0 + 1, fill: '#5d544c', seg: 8 });
  sline(ctx, headX - 3, headY - HEAD_R - 1.2, headX + 3, headY - HEAD_R - 2.2, { color: PALETTE.gold, width: 1.2, seed: seed0 + 2 });

  if (facing === 'down') {
    scircle(ctx, headX - 2, headY - 0.4, 0.9, { color: PALETTE.ink, width: 1, seed: seed0 + 3, seg: 6 });
    scircle(ctx, headX + 2, headY - 0.4, 0.9, { color: PALETTE.ink, width: 1, seed: seed0 + 4, seg: 6 });
    sline(ctx, headX - 1.6, headY + 2.2, headX + 1.6, headY + 2.2, { color: PALETTE.ink, width: 1, alpha: 0.7, seed: seed0 + 5 });
  } else if (side) {
    scircle(ctx, headX + dir * 1.6, headY - 0.4, 0.9, { color: PALETTE.ink, width: 1, seed: seed0 + 6, seg: 6 });
  } else {
    sline(ctx, headX - 3, headY + 1, headX + 3, headY + 1, { color: PALETTE.inkSoft, width: 1, alpha: 0.5, seed: seed0 + 7 });
  }
}

/** 玩家名牌（调试/联机预留） */
export function drawNameTag(ctx, x, y, text) {
  stext(ctx, text, x, y - 40, { font: '11px "Kaiti SC","STKaiti",serif', color: PALETTE.inkSoft });
}
